/**
 * LA FOTO DEL MAPA, GUARDADA EN EL TELÉFONO.
 *
 * El satélite del mapa son teselas de Esri de 256 px que la capa descarga una a
 * una (`esriSatelliteLayer`). Hasta ahora solo se recordaban 24, en memoria, así
 * que cada vez que se entraba al mapa se volvían a bajar todas: era lo que más
 * se notaba al abrir el mapa con mala cobertura. Con esto:
 *
 *   · una tesela vista una vez se pinta desde el teléfono, sin red;
 *   · la descarga completa (`sincronizacionOffline`) baja de antemano las teselas
 *     de las naves de la empresa, a los zooms en que se mira una nave.
 *
 * Las teselas cambian poco (Esri renueva la imagen cada muchos meses): se usa la
 * copia mientras tenga menos de `REFRESCO_MS`; pasado ese tiempo, con red se
 * baja otra vez, y sin red se sigue enseñando la vieja.
 */
import { usarAlmacenTeselas } from "./registroTeselas";
import { guardar, leer } from "./almacenOffline";
import { modoActivo, sinRedSegura, usuarioOffline } from "./estadoOffline";

const REFRESCO_MS = 180 * 24 * 60 * 60 * 1000;

interface TeselaGuardada {
  blob: Blob;
  at: number;
}

const claveTesela = (k: string) => `tesela:${k}`;

export function instalarTeselasOffline(): void {
  usarAlmacenTeselas({
    async leer(clave) {
      const uid = usuarioOffline();
      if (!uid || !modoActivo()) return null;
      const t = await leer<TeselaGuardada>("archivos", uid, claveTesela(clave));
      if (!t?.blob) return null;
      if (Date.now() - t.at > REFRESCO_MS && !sinRedSegura()) return null;
      return t.blob;
    },
    guardar(clave, blob) {
      const uid = usuarioOffline();
      if (!uid || !modoActivo() || !blob || blob.size === 0) return;
      void guardar("archivos", uid, claveTesela(clave), { blob, at: Date.now() } satisfies TeselaGuardada);
    },
  });
}

/* ─── Descarga de antemano ───────────────────────────────────────────────── */

/** Tesela (x, y) que contiene un punto a un zoom (proyección Web Mercator). */
export function teselaDe(lat: number, lng: number, z: number): { x: number; y: number } {
  const n = 2 ** z;
  const x = Math.floor(((lng + 180) / 360) * n);
  const rad = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n);
  return { x: Math.min(n - 1, Math.max(0, x)), y: Math.min(n - 1, Math.max(0, y)) };
}

export interface Caja {
  sur: number;
  oeste: number;
  norte: number;
  este: number;
}

/**
 * Las teselas que cubren una caja a cada zoom, con un margen de una tesela
 * alrededor (el mapa enseña algo más que la nave). Tope por zoom para que una
 * caja absurda (coordenadas mal) no pida miles.
 */
export function teselasDeCaja(caja: Caja, zooms: number[], margen = 1, topePorZoom = 400): string[] {
  const out: string[] = [];
  for (const z of zooms) {
    const a = teselaDe(caja.norte, caja.oeste, z);
    const b = teselaDe(caja.sur, caja.este, z);
    const x0 = Math.min(a.x, b.x) - margen;
    const x1 = Math.max(a.x, b.x) + margen;
    const y0 = Math.min(a.y, b.y) - margen;
    const y1 = Math.max(a.y, b.y) + margen;
    if ((x1 - x0 + 1) * (y1 - y0 + 1) > topePorZoom) continue;
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) out.push(`${z}/${x}/${y}`);
  }
  return out;
}

/** La caja de un anillo `[lng, lat][]` (GeoJSON) o `{lat,lng}[]`. */
export function cajaDe(puntos: Array<[number, number] | { lat: number; lng: number }>): Caja | null {
  let sur = Infinity;
  let norte = -Infinity;
  let oeste = Infinity;
  let este = -Infinity;
  for (const p of puntos) {
    const lng = Array.isArray(p) ? p[0] : p.lng;
    const lat = Array.isArray(p) ? p[1] : p.lat;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    sur = Math.min(sur, lat);
    norte = Math.max(norte, lat);
    oeste = Math.min(oeste, lng);
    este = Math.max(este, lng);
  }
  return Number.isFinite(sur) ? { sur, oeste, norte, este } : null;
}

const URL_ESRI = (k: string) => {
  const [z, x, y] = k.split("/");
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;
};

/**
 * Baja y guarda las teselas que falten. Devuelve cuántas bajó. De pocas en
 * pocas para no ahogar una red floja, y se para si se pierde la red o cambia el
 * usuario.
 */
export async function descargarTeselas(
  claves: string[],
  { enParalelo = 2, tope = 400, antesDeCada }: { enParalelo?: number; tope?: number; antesDeCada?: () => Promise<void> } = {},
): Promise<{ bajadas: number; quedan: number }> {
  const uid = usuarioOffline();
  if (!uid || !modoActivo()) return { bajadas: 0, quedan: 0 };
  // Solo las que faltan (o están viejas); como mucho `tope` por pasada.
  const faltan: string[] = [];
  for (const k of claves) {
    const ya = await leer<TeselaGuardada>("archivos", uid, claveTesela(k));
    if (!ya?.blob || Date.now() - ya.at >= REFRESCO_MS) faltan.push(k);
  }
  const ahora = faltan.slice(0, tope);
  let bajadas = 0;
  let i = 0;
  const trabajador = async () => {
    while (i < ahora.length) {
      const k = ahora[i++];
      if (sinRedSegura() || usuarioOffline() !== uid || !modoActivo()) return;
      await antesDeCada?.();
      try {
        const resp = await fetch(URL_ESRI(k));
        if (!resp.ok) continue;
        const blob = await resp.blob();
        if (blob.size > 0 && usuarioOffline() === uid) {
          await guardar("archivos", uid, claveTesela(k), { blob, at: Date.now() } satisfies TeselaGuardada);
          bajadas++;
        }
      } catch {
        /* una tesela que no baja no para las demás */
      }
    }
  };
  await Promise.all(Array.from({ length: enParalelo }, trabajador));
  return { bajadas, quedan: faltan.length - bajadas };
}
